import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Activity,
  ArrowLeft,
  ChevronRight,
  FolderOpen,
  History,
  Layers,
  RefreshCw,
  Save,
  Search
} from 'lucide-react';
import Avatar from '../components/ui/Avatar';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import Skeleton from '../components/ui/Skeleton';
import EntityActivityCard from '../components/admin/EntityActivityCard';
import DayActivityCard from '../components/admin/DayActivityCard';
import ReactivateModal from '../components/admin/ReactivateModal';
import { pathForTab } from '../utils/routeManagementHelpers';
import {
  getUserActivity,
  getEntityHistory,
  bulkDeleteStops,
  bulkDeleteBusRoutes
} from '../utils/api';
import {
  formatDate,
  formatDateTime,
  relativeTime,
  roleBadge
} from '../utils/commandCenterHelpers';

const ACTION_FILTERS = [
  { value: 'all', label: 'All actions' },
  { value: 'create', label: 'Created' },
  { value: 'update', label: 'Updated' },
  { value: 'import', label: 'Imported' },
  { value: 'delete', label: 'Deleted' },
  { value: 'baseline', label: 'Baseline' }
];

const SORTS = {
  recent: { label: 'Recent first', cmp: (a, b) => new Date(b.lastSavedAt) - new Date(a.lastSavedAt) },
  saves: { label: 'Most saves', cmp: (a, b) => b.saveCount - a.saveCount },
  name: { label: 'Name A–Z', cmp: (a, b) => a.entity.name.localeCompare(b.entity.name) }
};

export default function UserActivityPage() {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [viewMode, setViewMode] = useState('day'); // 'day' | 'entity'
  const [expandedEntities, setExpandedEntities] = useState(() => new Set());
  const [expandedDays, setExpandedDays] = useState(() => new Set());
  const [openVersions, setOpenVersions] = useState(() => new Set());
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [sort, setSort] = useState('recent');

  const [reactivateTarget, setReactivateTarget] = useState(null); // { entity, versions, version }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getUserActivity(userId);
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const byEntity = data?.byEntity || [];
  const feed = data?.feed || [];

  // Group the flat feed into calendar days (viewer's local timezone), each split
  // into Stops and Routes. Newest day first.
  const days = useMemo(() => {
    const query = search.trim().toLowerCase();
    let list = feed;
    if (query) {
      list = list.filter(
        (e) =>
          e.name.toLowerCase().includes(query) ||
          String(e.detail?.stopId || e.detail?.routeNumber || '')
            .toLowerCase()
            .includes(query)
      );
    }
    if (actionFilter !== 'all') list = list.filter((e) => e.action === actionFilter);

    const map = new Map();
    for (const e of list) {
      const d = new Date(e.savedAt);
      const key = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      if (!map.has(key)) map.set(key, { key, dateKey: key, ts: d.getTime(), stops: [], routes: [] });
      const bucket = map.get(key);
      (e.entityType === 'route' ? bucket.routes : bucket.stops).push(e);
      bucket.ts = Math.max(bucket.ts, d.getTime());
    }
    return [...map.values()].sort((a, b) => b.ts - a.ts);
  }, [feed, search, actionFilter]);

  const visibleGroups = useMemo(() => {
    let groups = [...byEntity];
    const query = search.trim().toLowerCase();
    if (query) {
      groups = groups.filter(
        (g) =>
          g.entity.name.toLowerCase().includes(query) ||
          g.entity.entityRef.toLowerCase().includes(query)
      );
    }
    if (actionFilter !== 'all') {
      groups = groups.filter((g) => g.versions.some((v) => v.action === actionFilter));
    }
    groups.sort(SORTS[sort].cmp);
    return groups;
  }, [byEntity, search, actionFilter, sort]);

  const toggleEntity = (id) =>
    setExpandedEntities((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleVersion = (id) =>
    setOpenVersions((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleDay = (id) =>
    setExpandedDays((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const expandAll = () => {
    if (viewMode === 'day') setExpandedDays(new Set(days.map((d) => d.key)));
    else setExpandedEntities(new Set(visibleGroups.map((g) => g.entity.entityRef)));
  };
  const collapseAll = () => {
    setExpandedEntities(new Set());
    setExpandedDays(new Set());
    setOpenVersions(new Set());
  };

  function onReactivate(entity, versions, version) {
    setReactivateTarget({ entity, versions, version });
  }

  // From a day-feed row: pull the entity's full timeline so the modal can diff
  // current vs. target, then open it on the chosen version.
  async function onReactivateItem(event) {
    try {
      const { data: history } = await getEntityHistory(event.entityRef);
      const target = history.versions.find((v) => v.id === event.versionId);
      if (!target) {
        toast.error('That version is no longer available');
        return;
      }
      setReactivateTarget({ entity: history.entity, versions: history.versions, version: target });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load version history');
    }
  }

  // Edit opens the real map editor in the workspace (drag-and-drop + coordinates),
  // deep-linked to this stop/route. Saving there writes to the DB and records a
  // version, exactly like editing from the create pages.
  function onEditItem(event) {
    // Straight to the tab that handles this entity: "/" would be redirected to the
    // dashboard, and a redirect does not carry router state, so the edit intent
    // would be lost on the way.
    navigate(pathForTab(event.entityType === 'route' ? 'routes' : 'stops'), {
      state: { edit: { type: event.entityType, id: event.entityRef } }
    });
  }

  // Soft-delete a stop/route from the activity view — moves it to Trash (where
  // it can be restored), then refreshes the feed.
  async function onDeleteItem(event) {
    const label = event.entityType === 'route' ? 'route' : 'stop';
    if (!window.confirm(`Delete this ${label}? It moves to Trash and can be restored.`)) return;
    try {
      if (event.entityType === 'route') {
        await bulkDeleteBusRoutes([event.entityRef]);
      } else {
        await bulkDeleteStops([event.entityRef]);
      }
      toast.success(`${label.charAt(0).toUpperCase()}${label.slice(1)} deleted`);
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Delete failed');
    }
  }

  function onReactivated() {
    load(); // refresh the whole activity view after a rollback
  }

  const user = data?.user;
  const totals = data?.totals;
  const badge = user ? roleBadge(user.role) : null;

  return (
    <>
        {/* breadcrumb */}
        <div className="flex items-center gap-1.5 text-[12.5px] text-slate-500 mb-4">
          <button
            type="button"
            onClick={() => navigate('/command-center')}
            className="inline-flex items-center gap-1.5 hover:text-slate-800 transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Command Center
          </button>
          <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
          <span className="text-slate-700 font-medium">{user?.name || 'User activity'}</span>
        </div>

        {/* profile card */}
        {loading && !user ? (
          <Skeleton className="h-44 rounded-3xl" />
        ) : (
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-950 via-slate-900 to-brand-800 text-white px-6 py-6 shadow-pop">
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-brand-500/30 blur-3xl" />
            <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
              <Avatar name={user?.name || '?'} size="lg" className="h-14 w-14 text-[18px] ring-white/20" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h1 className="text-[22px] font-semibold tracking-tight">{user?.name}</h1>
                  {badge && (
                    <Badge variant={badge.variant} size="md">
                      {badge.label}
                    </Badge>
                  )}
                </div>
                <p className="text-[13px] text-slate-300">@{user?.username}</p>
                <p className="mt-1 text-[12px] text-slate-400">
                  Created {formatDate(user?.createdAt)} · Last login{' '}
                  {user?.lastLoginAt ? relativeTime(user.lastLoginAt) : 'never'}
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={RefreshCw}
                onClick={load}
                loading={loading}
                className="bg-white/10 border-white/15 text-white hover:bg-white/20"
              >
                Refresh
              </Button>
            </div>

            {/* stat tiles */}
            <div className="relative mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <HeaderStat icon={Save} label="Total saves" value={totals?.totalSaves ?? 0} />
              <HeaderStat icon={Layers} label="Entities touched" value={totals?.entitiesTouched ?? 0} />
              <HeaderStat icon={History} label="Reactivations" value={totals?.reactivationsByUser ?? 0} />
              <HeaderStat
                icon={Activity}
                label="Last activity"
                value={totals?.lastActivityAt ? relativeTime(totals.lastActivityAt) : '—'}
                small
              />
            </div>

            {/* per-action breakdown */}
            {totals?.byAction && (
              <div className="relative mt-3 flex flex-wrap gap-2">
                <ActionCount label="Created" value={totals.byAction.create} />
                <ActionCount label="Updated" value={totals.byAction.update} />
                <ActionCount label="Imported" value={totals.byAction.import} />
                <ActionCount label="Reactivated" value={totals.byAction.rollback} />
                <ActionCount label="Baseline" value={totals.byAction.baseline} />
              </div>
            )}
          </div>
        )}

        {/* activity grouped by entity */}
        <div className="mt-5 rounded-2xl border border-slate-200 bg-white shadow-soft">
          <div className="px-5 py-4 border-b border-slate-100 flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="flex items-center gap-2 lg:flex-1">
              <span className="h-7 w-7 rounded-lg bg-brand-50 text-brand-600 inline-flex items-center justify-center">
                <FolderOpen className="h-4 w-4" />
              </span>
              <h2 className="text-[15px] font-semibold text-slate-900">
                {viewMode === 'day' ? 'Activity by day' : 'Activity grouped by entity'}
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
                <ViewToggleButton active={viewMode === 'day'} onClick={() => setViewMode('day')}>
                  By day
                </ViewToggleButton>
                <ViewToggleButton active={viewMode === 'entity'} onClick={() => setViewMode('entity')}>
                  By entity
                </ViewToggleButton>
              </div>
              <Button size="sm" variant="ghost" onClick={expandAll}>
                Expand all
              </Button>
              <Button size="sm" variant="ghost" onClick={collapseAll}>
                Collapse all
              </Button>
            </div>
          </div>

          <div className="px-5 py-3 border-b border-slate-100 flex flex-col sm:flex-row gap-2.5">
            <Input
              leftIcon={Search}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search entity name or id"
              className="sm:flex-1"
            />
            <Select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} className="sm:w-40">
              {ACTION_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </Select>
            <Select value={sort} onChange={(e) => setSort(e.target.value)} className="sm:w-40">
              {Object.entries(SORTS).map(([key, { label }]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </Select>
          </div>

          <div className="p-4 space-y-3">
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-16 rounded-xl" />
              ))
            ) : viewMode === 'day' ? (
              days.length === 0 ? (
                <div className="py-12 text-center">
                  <p className="text-[14px] font-medium text-slate-700">No activity to show</p>
                  <p className="text-[12.5px] text-slate-500 mt-1">
                    {feed.length === 0
                      ? 'This user has not changed any stops or routes yet.'
                      : 'No changes match the current filters.'}
                  </p>
                </div>
              ) : (
                days.map((day) => (
                  <DayActivityCard
                    key={day.key}
                    day={day}
                    expanded={expandedDays.has(day.key)}
                    onToggle={() => toggleDay(day.key)}
                    onEdit={onEditItem}
                    onReactivate={onReactivateItem}
                    onDelete={onDeleteItem}
                  />
                ))
              )
            ) : visibleGroups.length === 0 ? (
              <div className="py-12 text-center">
                <p className="text-[14px] font-medium text-slate-700">No activity to show</p>
                <p className="text-[12.5px] text-slate-500 mt-1">
                  {byEntity.length === 0
                    ? 'This user has not saved any entities yet.'
                    : 'No entities match the current filters.'}
                </p>
              </div>
            ) : (
              visibleGroups.map((group) => (
                <EntityActivityCard
                  key={group.entity.entityRef}
                  group={group}
                  expanded={expandedEntities.has(group.entity.entityRef)}
                  onToggle={() => toggleEntity(group.entity.entityRef)}
                  openVersions={openVersions}
                  onToggleVersion={toggleVersion}
                  onReactivate={onReactivate}
                />
              ))
            )}
          </div>
        </div>

        {/* reactivations performed */}
        {data?.reactivations?.length > 0 && (
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white shadow-soft">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <span className="h-7 w-7 rounded-lg bg-amber-50 text-amber-600 inline-flex items-center justify-center">
                <History className="h-4 w-4" />
              </span>
              <h2 className="text-[15px] font-semibold text-slate-900">Reactivations performed</h2>
              <Badge variant="warning" size="sm" className="ml-1">
                {data.reactivations.length}
              </Badge>
            </div>
            <ul className="divide-y divide-slate-100">
              {data.reactivations.map((r) => (
                <li key={r.versionId} className="px-5 py-3 flex items-center gap-3 text-[13px]">
                  <Badge variant="outline" size="sm" className="tabular-nums shrink-0">
                    V{r.versionNumber}
                  </Badge>
                  <span className="font-medium text-slate-800 truncate">{r.entity.name}</span>
                  <Badge variant="outline" size="sm" className="shrink-0">
                    {r.entity.label}
                  </Badge>
                  <span className="text-[12px] text-slate-400 truncate">
                    originally by {r.originalAuthor}
                  </span>
                  <span className="ml-auto text-[12px] text-slate-400 shrink-0">
                    {formatDateTime(r.reactivatedAt)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

      <ReactivateModal
        open={!!reactivateTarget}
        onClose={() => setReactivateTarget(null)}
        entity={reactivateTarget?.entity}
        versions={reactivateTarget?.versions}
        target={reactivateTarget?.version}
        onDone={onReactivated}
      />
    </>
  );
}

function ViewToggleButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'px-2.5 py-1 rounded-md text-[12px] font-medium transition-colors ' +
        (active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700')
      }
    >
      {children}
    </button>
  );
}

function HeaderStat({ icon: Icon, label, value, small }) {
  return (
    <div className="rounded-xl bg-white/10 border border-white/15 px-3 py-2.5 backdrop-blur">
      <div className="flex items-center gap-1.5 text-white/70">
        <Icon className="h-3.5 w-3.5" />
        <p className="text-[10.5px] uppercase tracking-wide">{label}</p>
      </div>
      <p className={`mt-1 font-semibold tabular-nums ${small ? 'text-[15px]' : 'text-[22px]'} leading-none`}>
        {value}
      </p>
    </div>
  );
}

function ActionCount({ label, value }) {
  return (
    <span className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full bg-white/10 border border-white/15 text-[11.5px] text-white/90">
      <span className="text-white/60">{label}</span>
      <span className="font-semibold tabular-nums">{value ?? 0}</span>
    </span>
  );
}
